import { NOTIFICATION_CATEGORIES, type NotificationCategory } from '@/types/notifications';

import { MessageAccessPrompt } from './MessageAccessPrompt';
import { useNotificationSettings } from './useNotificationSettings';

/**
 * Настройки уведомлений.
 *
 * Категории названы тем, что человек получит, а не тем, как они устроены
 * внутри: «Готовые портреты и фотосессии» вместо `generation`. Переключение
 * сохраняется сразу — отдельная кнопка «Сохранить» на экране согласий
 * лишний раз даёт повод уйти, не применив изменение.
 */
const LABELS: Record<NotificationCategory, { title: string; hint: string }> = {
  generation: {
    title: 'Готовые портреты и фотосессии',
    hint: 'Сообщим, когда серия досчитается — она идёт дольше одного портрета.',
  },
  bonus: {
    title: 'Начисления и бонусы',
    hint: 'Когда на балансе появляются новые генерации.',
  },
  referral: {
    title: 'Приглашения друзей',
    hint: 'Когда приглашённый создаёт свой первый портрет и награда начисляется.',
  },
  new_collection: {
    title: 'Новые коллекции',
    hint: 'Редко: только когда появляется новая коллекция.',
  },
  reminder: {
    title: 'Напоминания',
    hint: 'Например, что бесплатная генерация снова доступна.',
  },
  special_offer: {
    title: 'Специальные предложения',
    hint: 'Рекламные сообщения. По умолчанию выключено.',
  },
};

export function NotificationSettings() {
  const { preferences, channels, isPending, isError, refetch, save, saveStatus, saveError } =
    useNotificationSettings();

  if (isPending) {
    return <p className="px-6 py-8 text-text-2">Загрузка настроек…</p>;
  }

  if (isError) {
    return (
      <div className="px-6 py-8">
        <p className="text-danger">Не удалось загрузить настройки уведомлений.</p>
        <button type="button" onClick={refetch} className="mt-2 text-sm text-accent hover:underline">
          Повторить
        </button>
      </div>
    );
  }

  const categories = preferences?.categories ?? {};

  const toggle = (category: NotificationCategory) => {
    // Набор пишется целиком: частичное слияние разошлось бы с тем, что
    // человек видит на экране.
    const next = Object.fromEntries(
      NOTIFICATION_CATEGORIES.map((key) => [
        key,
        key === category ? !categories[key] : Boolean(categories[key]),
      ]),
    );

    save(next);
  };

  return (
    <div className="flex flex-col gap-4 px-6 py-6">
      <MessageAccessPrompt />

      {channels === 0 && (
        <p className="rounded-xl border border-border px-4 py-3 text-sm text-text-2">
          Доставка уведомлений пока не подключена — отправлять их некуда. Настройки сохранятся
          и начнут действовать, как только канал появится.
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {NOTIFICATION_CATEGORIES.map((category) => (
          <li key={category} className="flex items-start gap-3">
            <input
              id={`notification-${category}`}
              type="checkbox"
              checked={Boolean(categories[category])}
              onChange={() => toggle(category)}
              disabled={saveStatus === 'pending'}
              className="mt-1 size-4 accent-accent"
            />
            <label htmlFor={`notification-${category}`} className="flex flex-col">
              <span className="text-sm text-text">{LABELS[category].title}</span>
              <span className="text-xs text-text-3">{LABELS[category].hint}</span>
            </label>
          </li>
        ))}
      </ul>

      {saveStatus === 'error' && (
        <p className="text-sm text-danger">{saveError ?? 'Не удалось сохранить настройки.'}</p>
      )}

      {saveStatus === 'success' && <p className="text-sm text-text-3">Настройки сохранены.</p>}
    </div>
  );
}
